<template>
  <div>
    <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
      Bulk Create Pages
    </h2>

    <!-- Mode Tabs -->
    <div class="flex gap-4 mb-6 border-b border-gray-200 dark:border-gray-700">
      <button
        @click="mode = 'csv'"
        :class="mode === 'csv' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
        class="pb-3 border-b-2 text-sm font-medium"
      >
        CSV Upload
      </button>
      <button
        @click="mode = 'manual'"
        :class="mode === 'manual' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
        class="pb-3 border-b-2 text-sm font-medium"
      >
        Manual Entry
      </button>
    </div>

    <!-- CSV Mode -->
    <div v-if="mode === 'csv'">
      <div class="mb-4">
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Upload a CSV with columns: <code class="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">target_slug</code> (required),
          <code class="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">service</code>,
          <code class="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">city</code>,
          <code class="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">state</code>,
          <code class="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">business_name</code>
        </p>
      </div>
      <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
        <input type="file" ref="fileInput" accept=".csv" @change="handleFileSelect" class="hidden" />
        <button @click="$refs.fileInput.click()" class="btn btn-secondary btn-sm">
          Choose CSV File
        </button>
        <p v-if="csvFileName" class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {{ csvFileName }} ({{ csvRowCount }} rows)
        </p>
      </div>
    </div>

    <!-- Manual Mode -->
    <div v-if="mode === 'manual'">
      <div v-for="(entry, index) in manualEntries" :key="index" class="flex gap-3 mb-3 items-start">
        <UiInput v-model="entry.target_slug" label="Slug" placeholder="/austin-plumbing" class="flex-1" />
        <UiInput v-model="entry.service" label="Service" placeholder="Plumbing" class="flex-1" />
        <UiInput v-model="entry.city" label="City" placeholder="Austin" class="flex-1" />
        <UiInput v-model="entry.state" label="State" placeholder="TX" class="w-20" />
        <button @click="removeEntry(index)" class="mt-7 text-red-500 hover:text-red-700 text-sm">Remove</button>
      </div>
      <button @click="addEntry" class="text-sm text-primary-600 hover:text-primary-700">
        + Add another page
      </button>
    </div>

    <!-- Error -->
    <div v-if="error" class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg text-sm">
      {{ error }}
    </div>

    <!-- Actions -->
    <div class="mt-6 flex justify-end gap-3">
      <button @click="$emit('cancel')" class="btn btn-secondary btn-sm">Cancel</button>
      <button @click="handleSubmit" :disabled="submitting" class="btn btn-cta btn-sm">
        {{ submitting ? 'Creating...' : `Create ${entryCount} Pages` }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { bulkCreatePageBuilds } from '~/javascript/api/page_builds'

const props = defineProps({
  pageTemplateId: { type: String, required: true }
})

const emit = defineEmits(['cancel', 'created'])

const mode = ref('csv')
const csvContent = ref('')
const csvFileName = ref('')
const csvRowCount = ref(0)
const submitting = ref(false)
const error = ref(null)

const manualEntries = ref([
  { target_slug: '', service: '', city: '', state: '' }
])

const entryCount = computed(() => {
  if (mode.value === 'csv') return csvRowCount.value
  return manualEntries.value.filter(e => e.target_slug).length
})

const addEntry = () => {
  manualEntries.value.push({ target_slug: '', service: '', city: '', state: '' })
}

const removeEntry = (index) => {
  if (manualEntries.value.length > 1) {
    manualEntries.value.splice(index, 1)
  }
}

const handleFileSelect = (event) => {
  const file = event.target.files[0]
  if (!file) return

  csvFileName.value = file.name
  const reader = new FileReader()
  reader.onload = (e) => {
    csvContent.value = e.target.result
    const lines = csvContent.value.trim().split('\n')
    csvRowCount.value = Math.max(0, lines.length - 1)
  }
  reader.readAsText(file)
}

const handleSubmit = async () => {
  error.value = null
  submitting.value = true

  try {
    let payload
    if (mode.value === 'csv') {
      if (!csvContent.value) {
        error.value = 'Please select a CSV file'
        return
      }
      payload = { csv_content: csvContent.value }
    } else {
      const entries = manualEntries.value
        .filter(e => e.target_slug)
        .map(e => ({
          target_slug: e.target_slug.startsWith('/') ? e.target_slug : `/${e.target_slug}`,
          seo_variables: {
            service: e.service,
            city: e.city,
            state: e.state
          }
        }))

      if (entries.length === 0) {
        error.value = 'Add at least one page with a target slug'
        return
      }
      payload = { entries }
    }

    const { data } = await bulkCreatePageBuilds(props.pageTemplateId, payload)
    emit('created', data)
  } catch (err) {
    error.value = err.response?.data?.error || err.message
  } finally {
    submitting.value = false
  }
}
</script>
